import React, { useState, useRef, useEffect, KeyboardEvent, ChangeEvent, ClipboardEvent } from 'react';

export interface EmailChipInputProps {
    value?: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    placeholder?: string;
    isInvalid?: boolean;
    id?: string;
    name?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmail = (em: string) => em.trim().toLowerCase();

const parseEmails = (text?: string): string[] => {
    if (!text) return [];
    const seen = new Set<string>();
    return text
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter(Boolean)
        .filter((em) => {
            const norm = normalizeEmail(em);
            if (seen.has(norm)) return false;
            seen.add(norm);
            return true;
        });
};

export const EmailChipInput: React.FC<EmailChipInputProps> = ({
    value = '',
    onChange,
    onBlur,
    disabled = false,
    placeholder = 'напр. office@client.com, acc@client.com',
    isInvalid = false,
    id,
    name,
}) => {
    const [emails, setEmails] = useState<string[]>(() => parseEmails(value));
    const [inputValue, setInputValue] = useState<string>('');
    const [selectedChipIndex, setSelectedChipIndex] = useState<number | null>(null);
    const [editingChipIndex, setEditingChipIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>('');

    const mainInputRef = useRef<HTMLInputElement>(null);
    const editInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync from external value changes (e.g. form reset / initial load)
    useEffect(() => {
        const parsed = parseEmails(value);
        if (parsed.join(', ') !== emails.join(', ')) {
            setEmails(parsed);
        }
    }, [value]);

    // Focus inline edit input when entering edit mode
    useEffect(() => {
        if (editingChipIndex !== null && editInputRef.current) {
            editInputRef.current.focus();
            editInputRef.current.select();
        }
    }, [editingChipIndex]);

    const updateEmails = (nextEmails: string[]) => {
        setEmails(nextEmails);
        onChange(nextEmails.join(', '));
    };

    const commitInput = (rawText: string) => {
        const tokens = parseEmails(rawText);
        if (tokens.length === 0) return;

        const existingNormalized = new Map<string, number>();
        emails.forEach((em, idx) => existingNormalized.set(normalizeEmail(em), idx));

        const uniqueNewTokens: string[] = [];
        let duplicateFoundIndex: number | null = null;

        for (const token of tokens) {
            const norm = normalizeEmail(token);
            if (existingNormalized.has(norm)) {
                duplicateFoundIndex = existingNormalized.get(norm)!;
            } else {
                existingNormalized.set(norm, emails.length + uniqueNewTokens.length);
                uniqueNewTokens.push(token);
            }
        }

        if (uniqueNewTokens.length > 0) {
            const nextEmails = [...emails, ...uniqueNewTokens];
            updateEmails(nextEmails);
            setSelectedChipIndex(null);
        } else if (duplicateFoundIndex !== null) {
            // Highlight the existing duplicate chip for visual feedback
            setSelectedChipIndex(duplicateFoundIndex);
        }

        setInputValue('');
        setEditingChipIndex(null);
    };

    const handleMainInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (selectedChipIndex !== null) {
            setSelectedChipIndex(null);
        }
        setInputValue(val);
    };

    const handleMainInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        // 1. Space, Tab, Enter, Comma, Semicolon -> Commit email chip
        if (e.key === ' ' || e.code === 'Space' || e.key === 'Tab' || e.key === 'Enter' || e.key === ',' || e.key === ';') {
            if (inputValue.trim().length > 0) {
                e.preventDefault();
                commitInput(inputValue);
            } else if (e.key === ' ' || e.key === ',' || e.key === ';') {
                e.preventDefault();
            }
            // If empty and Tab is pressed, default browser focus movement occurs
            return;
        }

        // 2. Backspace behavior
        if (e.key === 'Backspace') {
            if (inputValue === '') {
                if (selectedChipIndex === null) {
                    // 1st Backspace: Select / highlight the last chip
                    if (emails.length > 0) {
                        e.preventDefault();
                        setSelectedChipIndex(emails.length - 1);
                    }
                } else {
                    // 2nd Backspace: Delete the selected chip
                    e.preventDefault();
                    const nextEmails = emails.filter((_, idx) => idx !== selectedChipIndex);
                    updateEmails(nextEmails);
                    setSelectedChipIndex(null);
                }
            }
            return;
        }

        // 3. Escape -> Cancel selection
        if (e.key === 'Escape') {
            setSelectedChipIndex(null);
            return;
        }

        // If typing any other character, unselect active chip
        if (selectedChipIndex !== null && e.key.length === 1) {
            setSelectedChipIndex(null);
        }
    };

    const handleMainInputBlur = () => {
        if (inputValue.trim().length > 0) {
            commitInput(inputValue);
        }
        onBlur?.();
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        const pastedText = e.clipboardData.getData('text');
        const tokens = parseEmails(pastedText);
        if (tokens.length > 0) {
            e.preventDefault();
            const existingNormalized = new Set(emails.map(normalizeEmail));
            const uniqueNew = tokens.filter((t) => !existingNormalized.has(normalizeEmail(t)));
            if (uniqueNew.length > 0) {
                const nextEmails = [...emails, ...uniqueNew];
                updateEmails(nextEmails);
            }
            setInputValue('');
            setSelectedChipIndex(null);
        }
    };

    const handleChipClick = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;

        if (selectedChipIndex === index) {
            // 2nd Click on the same chip: Enter inline edit mode
            setEditingChipIndex(index);
            setEditValue(emails[index]);
            setSelectedChipIndex(null);
        } else {
            // 1st Click: Activate/Select the chip
            setSelectedChipIndex(index);
            setEditingChipIndex(null);
        }
    };

    const handleChipDoubleClick = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;

        setEditingChipIndex(index);
        setEditValue(emails[index]);
        setSelectedChipIndex(null);
    };

    const handleRemoveChip = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;

        const nextEmails = emails.filter((_, idx) => idx !== index);
        updateEmails(nextEmails);
        if (selectedChipIndex === index) setSelectedChipIndex(null);
        if (editingChipIndex === index) setEditingChipIndex(null);
        mainInputRef.current?.focus();
    };

    const commitInlineEdit = (index: number) => {
        const trimmed = editValue.trim();
        let nextEmails: string[];

        if (!trimmed) {
            // If cleared completely, remove the chip
            nextEmails = emails.filter((_, idx) => idx !== index);
        } else {
            const tokens = parseEmails(trimmed);
            if (tokens.length > 1) {
                // If user entered multiple emails during edit, insert unique ones
                const existingNormalized = new Set(
                    emails.filter((_, i) => i !== index).map(normalizeEmail)
                );
                const uniqueTokens = tokens.filter((t) => !existingNormalized.has(normalizeEmail(t)));
                nextEmails = [
                    ...emails.slice(0, index),
                    ...uniqueTokens,
                    ...emails.slice(index + 1),
                ];
            } else {
                const newEmail = tokens[0] || trimmed;
                const norm = normalizeEmail(newEmail);
                const alreadyExistsElsewhere = emails.some(
                    (em, i) => i !== index && normalizeEmail(em) === norm
                );

                if (alreadyExistsElsewhere) {
                    // Remove duplicate instance
                    nextEmails = emails.filter((_, idx) => idx !== index);
                } else {
                    nextEmails = emails.map((em, idx) => (idx === index ? newEmail : em));
                }
            }
        }

        updateEmails(nextEmails);
        setEditingChipIndex(null);
        mainInputRef.current?.focus();
    };

    const handleEditKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ' || e.key === ',' || e.key === ';') {
            e.preventDefault();
            commitInlineEdit(index);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditingChipIndex(null);
            mainInputRef.current?.focus();
        }
    };

    const handleContainerClick = () => {
        if (disabled) return;
        setSelectedChipIndex(null);
        mainInputRef.current?.focus();
    };

    return (
        <div
            ref={containerRef}
            className={`email-chip-container ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={handleContainerClick}
        >
            {emails.map((email, index) => {
                const isEditing = editingChipIndex === index;
                const isSelected = selectedChipIndex === index;
                const isValid = EMAIL_REGEX.test(email);

                if (isEditing) {
                    return (
                        <input
                            key={`edit-${index}`}
                            ref={editInputRef}
                            type='text'
                            className='email-chip-edit-input'
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onKeyDown={(e) => handleEditKeyDown(index, e)}
                            onBlur={() => commitInlineEdit(index)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    );
                }

                return (
                    <span
                        key={`chip-${index}-${email}`}
                        className={`email-chip ${isSelected ? 'is-selected' : ''} ${!isValid ? 'is-invalid-email' : ''}`}
                        onClick={(e) => handleChipClick(index, e)}
                        onDoubleClick={(e) => handleChipDoubleClick(index, e)}
                        title={
                            !isValid
                                ? 'Невалиден имейл формат. Кликнете 2 пъти за редакция.'
                                : 'Кликнете 1 път за селекция, 2 пъти за редакция.'
                        }
                    >
                        {!isValid && <span style={{ marginRight: 2 }}>⚠️</span>}
                        <span className='email-chip-text'>{email}</span>
                        {!disabled && (
                            <span
                                className='email-chip-remove'
                                onClick={(e) => handleRemoveChip(index, e)}
                                title='Изтрий имейла'
                            >
                                &times;
                            </span>
                        )}
                    </span>
                );
            })}

            <input
                ref={mainInputRef}
                id={id}
                name={name}
                type='text'
                className='email-chip-main-input'
                value={inputValue}
                placeholder={emails.length === 0 ? placeholder : ''}
                onChange={handleMainInputChange}
                onKeyDown={handleMainInputKeyDown}
                onBlur={handleMainInputBlur}
                onPaste={handlePaste}
                disabled={disabled}
            />
        </div>
    );
};

export default EmailChipInput;
